import React from "react";
import { Button as BootstrapButton } from "react-bootstrap";
import "./Util.css";

/**
 * Reusable Custom Button Component
 * @param {string} variant - primary | secondary | danger | success | outline | dark | custom
 * @param {string} size - sm | md | lg
 * @param {function} onClick - Click handler
 * @param {ReactNode} children - Button content
 * @param {string} className - Additional custom classes
 * @param {boolean} disabled - Disabled state
 * @param {boolean} isLoading - Loading state (shows spinner and disables button)
 * @param {object} style - Additional inline styles (use sparingly)
 * @param {ReactNode} icon - Optional icon component
 * @param {string} bgColor - Custom background color (hex code like "#FF0000")
 * @param {string} textColor - Custom text color (hex code like "#FFFFFF")
 * @param {string} borderColor - Custom border color (hex code like "#000000")
 * @param {boolean} border - Show border (default: true for outline/secondary, false for others)
 * @param {boolean} fullWidth - Full width button
 * @param {string} width - Custom width in pixels (e.g., "120px")
 * @param {string} height - Custom height in pixels (e.g., "40px")
 * @param {string} borderRadius - Custom border radius in pixels (e.g., "8px", "16px")
 */
const CustomButton = ({ variant = "primary", size = "md", onClick, children, className = "", disabled = false, isLoading = false, style = {}, icon = null, fullWidth = false, bgColor = null, textColor = null, borderColor = null, border = null, width = null, height = null, borderRadius = null, ...props }) => {
  const effectiveVariant = bgColor || textColor ? "custom" : variant;

  const variantClasses = {
    primary: "backgroundPrimary text-white",
    secondary: "bg-white text-black",
    danger: "bg-danger text-white",
    success: "backgroundGreen text-white",
    outline: "bg-white text-black",
    dark: "bg-black text-white",
    custom: "",
  };

  const sizeClasses = {
    sm: "px-3 py-2 fs-13 ms-2",
    md: "px-4 py-2 fs-14 ms-2",
    lg: "px-5 py-3 fs-16 ms-2",
  };

  const showBorder = border !== null ? border : effectiveVariant === "outline" || effectiveVariant === "secondary";

  const customStyles = {
    ...(!width && !fullWidth && { width: "fit-content" }),
    ...(bgColor && { backgroundColor: bgColor }),
    ...(textColor && { color: textColor }),
    ...(borderColor && { borderColor: borderColor }),
    ...(width && { width: width }),
    ...(height && { height: height }),
    ...(borderRadius && { borderRadius: borderRadius }),
    ...(isLoading && { opacity: 1, pointerEvents: "none" }),
    ...style,
  };

  const buttonClasses = `
    ${effectiveVariant === "custom" ? "" : variantClasses[effectiveVariant] || variantClasses.primary}
    ${sizeClasses[size] || sizeClasses.md}
    ${fullWidth ? "w-100" : ""}
    ${showBorder ? "border" : "border-0"}
    ${borderRadius ? "" : "rounded-pill-100"}
    fw-700
    d-flex
    align-items-center
    justify-content-center
    gap-2
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  return (
    <BootstrapButton className={buttonClasses} onClick={onClick} disabled={disabled || isLoading} style={customStyles} {...props}>
      {isLoading ? (
        <div className="dot-loading">
          <span></span>
          <span></span>
          <span></span>
        </div>
      ) : (
        <>
          {icon && <span className="d-flex align-items-center">{icon}</span>}
          {children}
        </>
      )}
    </BootstrapButton>
  );
};

export default CustomButton;
